import mapboxgl from 'mapbox-gl'
import getDistance from 'geolib/es/getDistance'
import * as turf from '@turf/turf';
import { formatDistance } from '../formatting/units';

const SOURCE_LINE = 'controls-source-line'
const SOURCE_LINE2 = 'controls-source-line2'
const MAIN_COLOR = '#263238'
const HALO_COLOR = '#fff'
const HALO1_COLOR = '#0f0'
const HALO2_COLOR = '#ff0000'
const MEASURE_CURSOR = 'pointer'
const MEASURE_POINT_CURSOR = 'grab'
const MEASURE_POINT_DRAG_CURSOR = 'grabbing'

function computeLengthMeters(coordinates = []) {
  let dist = 0;
  for (let i = 0; i < coordinates.length - 1; i++) {
      const p1 = coordinates[i];
      const p2 = coordinates[i + 1];
      const line = {
          type: 'Feature',
          properties: {},
          geometry: {
              type: 'LineString',
              coordinates: [p1,p2]
          }
      };
    dist += turf.length(line, {units: "meters"});
  }
  return dist;
}

function formatSegmentDistance(meters, distanceUnits) {
  return formatDistance(meters, distanceUnits || 'metric', 2, true, true);
}

function geoLineString (coordinates = [], split = false, distanceUnits = 'metric') {
    if (split) {
        const features = [];
        for (let i = 0; i < coordinates.length - 1; i++) {
            const p1 = coordinates[i];
            const p2 = coordinates[i + 1];
            const line = {
                type: 'Feature',
                properties: {},
                geometry: {
                    type: 'LineString',
                    coordinates: [p1,p2]
                }
            };
            const length = turf.length(line, {units: "meters"});
            line.properties.length = formatSegmentDistance(length, distanceUnits);
            features.push(line);
        }

        return {
            "type": "FeatureCollection",
            "features": features
        }
    }
    return {
        "type": "FeatureCollection",
        "features": [
            {
                type: 'Feature',
                properties: {},
                geometry: {
                    type: 'LineString',
                    coordinates
                }
            }
        ]
    }
}

function defaultLabelFormat (delta, sum, units) {
  return `* ${delta.toFixed(2)} ${units}<br/>= ${sum.toFixed(2)} ${units}`
}

export default class RulerControl {
  constructor (options = {}) {
    this.onMeasureStateChange = options.onMeasureStateChange || (() => {});
    this.getDistanceUnits = options.getDistanceUnits || (() => 'metric');
    this.isMeasuring = false
    this.enabled = true
    this.markers = []
    this.layers = []
    this.coordinates = []
    this.labels = []
    this.units = options.units || 'km'
    this.font = options.font || ['open-sans']
    this.fontSize = options.fontSize || 12
    this.fontHalo = options.fontHalo || 1
    this.labelFormat = options.labelFormat || defaultLabelFormat
    this.mainColor = options.mainColor || MAIN_COLOR
    this.secondaryColor = options.secondaryColor || HALO_COLOR
    this.altColor = options.altColor || HALO1_COLOR
    this.mapClickListener = this.mapClickListener.bind(this)
    this.styleLoadListener = this.styleLoadListener.bind(this)
    this.applyMeasureCursor = this.applyMeasureCursor.bind(this)
  }

  insertControls () {
    this.container = document.createElement('div')
    this.container.classList.add('mapboxgl-ctrl')
    this.container.classList.add('mapboxgl-ctrl-group')
    this.container.classList.add('mapboxgl-ctrl-ruler')
    this.button = document.createElement('button')
    this.button.setAttribute('type', 'button')
    this.img = document.createElement('img')
    this.img.style.marginLeft = "3px"
    this.img.src = '/img/icon-ruler.svg'
    this.button.appendChild(this.img)
    this.container.appendChild(this.button)
  }

  setUnits (units) {
    this.units = units
  }

  getActiveDistanceUnits () {
    try {
      return this.getDistanceUnits() || 'metric';
    } catch (e) {
      return 'metric';
    }
  }

  applyMeasureCursor () {
    if (!this.isMeasuring || !this.map?.getCanvas()) {
      return;
    }
    this.map.getCanvas().style.cursor = MEASURE_CURSOR;
  }

  emitMeasureState () {
    const lengthMeters = computeLengthMeters(this.coordinates);
    this.onMeasureStateChange({
      measuring: true,
      coordinates: this.coordinates,
      lengthMeters,
      totalDistance: formatSegmentDistance(
        lengthMeters,
        this.getActiveDistanceUnits(),
      ),
    });
  }

  updateLineSources () {
    const distanceUnits = this.getActiveDistanceUnits();
    if (this.map.getSource(SOURCE_LINE)) {
      this.map.getSource(SOURCE_LINE).setData(geoLineString(this.coordinates));
    }
    if (this.map.getSource(SOURCE_LINE2)) {
      this.map.getSource(SOURCE_LINE2).setData(
        geoLineString(this.coordinates, true, distanceUnits),
      );
    }
  }

  // Call when depth/distance units preference changes so labels refresh.
  refreshDistanceUnits () {
    if (!this.isMeasuring) {
      return;
    }
    this.updateLineSources();
    this.emitMeasureState();
  }

  draw () {
    const distanceUnits = this.getActiveDistanceUnits();
    this.map.addSource(SOURCE_LINE, {
      type: 'geojson',
      data: geoLineString(this.coordinates),
      lineMetrics: true
    })
    this.map.addSource(SOURCE_LINE2, {
      type: 'geojson',
      data: geoLineString(this.coordinates, true, distanceUnits),
      lineMetrics: true
    })

    this.layers = [
        {
            id: 'ruler_1',
            type: 'line',
            source: SOURCE_LINE,
            paint: {
                'line-color': '#000000',
                'line-width': 10,
                'line-opacity': 0.9
            },
            layout: {
                'line-join': 'round'
            }
        },
        {
            id: 'ruler_2',
            type: 'line',
            source: SOURCE_LINE,
            paint: {
                'line-color': '#ffffff',
                'line-width': 3,
                'line-dasharray': [2.5,5,2.5],
                'line-offset': 1.5,
                'line-opacity': 1
            },
            layout: {
                'line-join': 'round'
            }
        },
        {
            id: 'ruler_5',
            type: 'line',
            source: SOURCE_LINE,
            paint: {
                'line-color': '#ffffff',
                'line-width': 3,
                'line-dasharray': [0,2.5,5,2.5],
                'line-offset': -1.5,
                'line-opacity': 1
            },
            layout: {
                'line-join': 'round'
            }
        },
        {
            id: 'ruler_3',
            type: 'symbol',
            source: SOURCE_LINE2,
            layout: {
                'text-field': ['get', 'length'],
                'text-font': ['open-sans'],
                'symbol-placement': 'line-center',
                'text-ignore-placement': true,
                'text-allow-overlap': true,
                'text-size': 20,
            },
            paint: {
                'text-color': '#ffffff',
                'text-translate': [0.0, 0.0],
                'text-translate-anchor': 'viewport',
                'text-opacity': 1.0,
                'text-halo-color': '#000000',
                'text-halo-width': 3,
                'text-halo-blur': 0,
                'text-occlusion-opacity': 1.0
            }
        }
    ]
    for (const layer of this.layers) {
        this.map.addLayer(layer);
    }
  }

  measuringOn () {
    this.isMeasuring = true
    this.markers = []
    this.coordinates = []
    this.labels = []
    this.button.style.background = 'rgb(34, 128, 216)';
    this.img.src = '/img/icon-ruler2.svg'
    this.applyMeasureCursor()
    this.button.classList.add('active')
    this.draw()
    this.map.on('click', this.mapClickListener)
    this.map.on('style.load', this.styleLoadListener)
    this.map.on('mousemove', this.applyMeasureCursor)
    this.map.fire('ruler.on')
    this.emitMeasureState();
    this.updateUrl();
  }

  measuringOff () {
    this.button.style.background = 'white';
    this.img.src = '/img/icon-ruler.svg'
    this.isMeasuring = false
    this.map.getCanvas().style.cursor = ''
    this.button.classList.remove('active')
    for (const layer of this.layers) {
        if (this.map.getLayer(layer.id)) this.map.removeLayer(layer.id)
    }

    if (this.map.getSource(SOURCE_LINE)) this.map.removeSource(SOURCE_LINE)
    if (this.map.getSource(SOURCE_LINE2)) this.map.removeSource(SOURCE_LINE2)
    this.markers.forEach((m) => m.remove())
    this.map.off('click', this.mapClickListener)
    this.map.off('style.load', this.styleLoadListener)
    this.map.off('mousemove', this.applyMeasureCursor)
    this.map.fire('ruler.off')
    this.onMeasureStateChange(null);
    this.updateUrl();
  }

  enable () {
    this.enabled = true
    this.button.classList.remove('disabled')
  }

  disable () {
    this.enabled = false
    this.measuringOff()
    this.button.classList.add('disabled')
  }

  mapClickListener (event) {
    const markerNode = document.createElement('div')
    markerNode.className = 'measure-point-marker'
    markerNode.style.width = '11px'
    markerNode.style.height = '11px'
    markerNode.style.borderRadius = '50%'
    markerNode.style.background = this.markers.length === 0 ? this.altColor : this.secondaryColor
    markerNode.style.boxSizing = 'border-box'
    markerNode.style.border = `2px solid #000000`
    markerNode.style.boxShadow = `0px 0px 0px 4px rgba(0,0,0,0.2)`;
    markerNode.style.cursor = MEASURE_POINT_CURSOR;
    const marker = new mapboxgl.Marker({
      element: markerNode,
      draggable: true
    })
      .setLngLat(event.lngLat)
      .addTo(this.map)
    this.coordinates.push([event.lngLat.lng, event.lngLat.lat])
    this.updateLineSources()
    this.emitMeasureState();
    this.updateUrl();

    this.markers.push(marker)
    if (this.markers.length > 1) {
      this.labels = this.coordinatesToLabels()
    }
    marker.on('dragstart', () => {
      markerNode.style.cursor = MEASURE_POINT_DRAG_CURSOR;
    })
    marker.on('drag', () => {
      const index = this.markers.indexOf(marker)
      const lngLat = marker.getLngLat()
      this.coordinates[index] = [lngLat.lng, lngLat.lat]
      this.labels = this.coordinatesToLabels()
      this.labels.forEach((l, i) => {
        const popup = this.markers[i].getPopup()
        if (popup) popup.setHTML(l)
      })
      this.updateLineSources()
      this.emitMeasureState();
      this.updateUrl();
    })
    marker.on('dragend', () => {
      markerNode.style.cursor = MEASURE_POINT_CURSOR;
      this.applyMeasureCursor();
    })
  }

  coordinatesToLabels () {
    const { coordinates, units, labelFormat } = this
    let sum = 0
    return coordinates.map((coordinate, index) => {
      if (index === 0) return labelFormat(0, 0, units)
      let delta = getDistance({
        latitude: coordinates[index - 1][1],
        longitude: coordinates[index - 1][0]
      }, {
        latitude: coordinates[index][1],
        longitude: coordinates[index][0]
      }) / 1000
      if (units === 'mi') {
        delta = delta * 0.621371
      } else if (units === 'nmi') {
        delta = delta * 0.539957
      }
      sum += delta
      return labelFormat(delta, sum, units)
    })
  }

  styleLoadListener () {
    this.draw()
  }

  onButtonClick () {
    if (!this.enabled) return
    if (this.isMeasuring) this.measuringOff()
    else this.measuringOn()
    this.map.fire('ruler.buttonclick', { measuring: this.isMeasuring })
  }

  updateUrl() {
    const url = new URL(window.location.href);

    if (this.isMeasuring && this.coordinates.length >= 2) {
        url.searchParams.set('measureState', btoa(JSON.stringify({
          c: this.coordinates.map((c2) => ([parseFloat(c2[0].toFixed(6)), parseFloat(c2[1].toFixed(6))]))
        })));
    } else {
        url.searchParams.delete('measureState'); 
    }
    window.history.pushState(null, '', url.toString());
  }

  onAdd (map) {
    this.map = map
    this.insertControls()
    this.button.addEventListener('click', this.onButtonClick.bind(this))

    try {
      const params = new URLSearchParams(window.location.search);
      if (params.has('measureState')) {
          const newMeasureState = JSON.parse(atob(params.get('measureState')));
          this.measuringOn();
          for (const coordinate of newMeasureState.c || []) {
              this.mapClickListener({
                  lngLat: {
                      lng:coordinate[0],
                      lat:coordinate[1]
                  }
              });
          }
      } 
    } catch (e) {
      console.log(e);
    }

    return this.container
  }

  onRemove () {
    if (this.isMeasuring) {
      this.measuringOff()
    }
    this.button.removeEventListener('click', this.onButtonClick.bind(this))
    this.map.off('click', this.mapClickListener)
    this.container.parentNode.removeChild(this.container)
    this.map = undefined
  }
}
